import { Component, h } from 'preact';
import linkState from 'linkstate';
import cx from 'classnames';

import { FILTERABLE_FE_STATUSES } from '../../../../../constants/api';
import { propertyTypes } from '../../../../../constants/properties';
import { PropertyDivision } from '../../../../../types';
import { ChoicesButtonGroup } from '../../common/choices-button-group.component';
import { LinearLocationSearch } from '../../common/linear-location-search.component';
import { FilterViewModel } from './filter-view-model';
import { DatePickerControl } from '../../common/date-picker-control.component';

interface VlPropertySearchPanelProps {
  onSearch?: (filterViewModel: FilterViewModel) => void;
  onClose?: () => void;
}
interface VlPropertySearchPanelState {
  filterModel?: FilterViewModel;
}

export class VlPropertySearchPanel extends Component<VlPropertySearchPanelProps, VlPropertySearchPanelState> {
  constructor(props) {
    super(props);
    this.state = {
      filterModel: {}
    };
  }

  onInput(input: FilterViewModel) {
    const filterModel = { ...this.state.filterModel, ...input };

    if (input.locationSearchString === '') {
      filterModel.location = null;
    }

    if (input.location) {
      filterModel.locationSearchString = input.location.label;
    }

    // Prevent division-type mismatches
    if (input.division && filterModel.propertyType) {
      const typeDefn = propertyTypes.find(type => type.label === filterModel.propertyType)
      if (typeDefn.division !== input.division) {
        delete filterModel.propertyType;
      }
    }

    this.setState({ filterModel });
  }

  onSearchClick() {
    if (this.props.onSearch) {
      this.props.onSearch(this.state.filterModel);
    }
  }

  render() {
    const state = this.state;
    const filter = state.filterModel;

    return (
      <div className="hi-vlsearchpanel">
        <div className="hi-vlsearchpanel__title-bar">
          <button className="btn btn-link hi-vlsearchpanel__title-back" onClick={ this.props.onClose }>
            <i className="fas fa-arrow-left" />
          </button>
          <span className="hi-vlsearchpanel__title-label">Search listing</span>
        </div>

        <div className="hi-vlsearchpanel__content">
          <div className="hi-form-group">
            <label className="hi-input-group__label">Uploader</label>
            <div className="d-flex flex-column form-check">
              { (['All', 'Direct only', 'Network only']).map(uploader => (
                <label className="hi-input-group__checkbox mr-0">
                  <input className="form-check-input" type="radio" name="uploaderGroup"
                    onClick={ () => this.onInput({ uploader }) }
                    checked={ (uploader === 'All' && !filter.uploader) || filter.uploader === uploader } />
                  { uploader }
                </label>
              )) }
            </div>
          </div>
            
          <div className="hi-form-group">
            <label className="hi-input-group__label">Property ID</label>
            <input type="text" className="form-control" placeholder="ID"
              value={ filter.propertyKey }
              onInput={ linkState(this, 'filterModel.propertyKey') } />
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Location</label>
            <LinearLocationSearch onInput={ locationSearchString => this.onInput({ locationSearchString }) }
              onLocationChange={ location => this.onInput({ location })}
              searchString={ state.filterModel.locationSearchString }
              location={ state.filterModel.location }
              placeholder='Select' />
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Uploader Name</label>
            <input type="text" className="form-control" placeholder="Uploader name"
              onInput={ linkState(this, 'filterModel.uploaderName') } />
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Sales Director Name</label>
            <input type="text" className="form-control" placeholder="Sales director name"
              onInput={ linkState(this, 'filterModel.salesDirector') } />
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Pre-selling</label>
            <div className="d-flex flex-column form-check">
              { (['All', 'No Pre-selling', 'Pre-selling only']).map(option => (
                <label className="hi-input-group__checkbox mr-0">
                  <input className="form-check-input" type="radio" name="preSale"
                    checked={ (option === 'All' && !filter.preSale) || filter.preSale === option }
                    onClick={ () => this.onInput({ preSale: option }) } />
                  { option }
                </label>
              )) }
            </div>
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Property Kind</label>
            <ChoicesButtonGroup choices={ ['Residential', 'Commercial'] }
              selectedChoice={ filter.division }
              onSelect={ (division: PropertyDivision) => this.onInput({ division }) } />
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Category</label>
            <ChoicesButtonGroup choices={ ['For rent', 'For sale'] }
              selectedChoice={ filter.categoryLabel }
              onSelect={ categoryLabel => this.onInput({ categoryLabel }) } />
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Status</label>
            <div className="d-flex flex-column form-check">
              { FILTERABLE_FE_STATUSES.map(feStatus => (
                <label className="hi-input-group__checkbox mr-0">
                  <input className="form-check-input" type="radio" name="feStatus"
                    checked={ filter.feStatus === feStatus }
                    onClick={ () => this.onInput({ feStatus }) } />
                  { feStatus }
                </label>
              )) }
            </div>
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Photos</label>
            <div className="d-flex flex-column form-check">
              { ([ 'All', 'With photos', 'Without photos' ]).map(status => (
                <label className="hi-input-group__checkbox mr-0">
                  <input className="form-check-input" type="radio" name="withPhoto"
                    onClick={ () => this.onInput({ images: status }) }
                    checked={ (status === 'All' && !filter.images) || filter.images === status } />
                  { status }
                </label>
              )) }
            </div>
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Date Modified (From - To)</label>
            <div className="d-flex">
              <DatePickerControl className="form-control w-50" placeholder="From"
                aria-label="Date modified from"
                onChange={ linkState(this, 'filterModel.dateModifiedFrom') }
                value={ state.filterModel.dateModifiedFrom } />
              <DatePickerControl className="form-control w-50" placeholder="To"
                aria-label="Date modified to"
                onChange={ linkState(this, 'filterModel.dateModifiedTo') }
                value={ state.filterModel.dateModifiedTo } />
            </div>
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Expires On (From - To)</label>
            <div className="d-flex">
              <DatePickerControl className="form-control w-50" placeholder="From"
                aria-label="Expires on from"
                onChange={ linkState(this, 'filterModel.dateExpiresFrom') }
                value={ state.filterModel.dateExpiresFrom } />
              <DatePickerControl className="form-control w-50" placeholder="To"
                aria-label="Expires on to"
                onChange={ linkState(this, 'filterModel.dateExpiresTo') }
                value={ state.filterModel.dateExpiresTo } />
            </div>
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Upload Date (From - To)</label>
            <div className="d-flex">
              <DatePickerControl className="form-control w-50" placeholder="From"
                aria-label="Upload Date from"
                onChange={ linkState(this, 'filterModel.dateCreatedFrom') }
                value={ state.filterModel.dateCreatedFrom } />
              <DatePickerControl className="form-control w-50" placeholder="To"
                aria-label="Upload Date to"
                onChange={ linkState(this, 'filterModel.dateCreatedTo') }
                value={ state.filterModel.dateCreatedTo } />
            </div>
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Property Type</label>
            <div className="d-flex flex-column form-check">
              { propertyTypes.filter(
                  propType => !filter.division || propType.division == filter.division
                ).map(propType => (
                  <label className="hi-input-group__checkbox mr-0">
                    <input className="form-check-input" type="radio" name="propertyType"
                      checked={ propType.label === filter.propertyType }
                      onClick={ () => this.onInput({ propertyType: propType.label }) } />
                    { propType.label }
                  </label>
                )) }
            </div>
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Price Range</label>
            <div className="d-flex">
              <input type="number"
                className={ cx('form-control flex-fill', {
                  'is-invalid': parseFloat(filter.minPrice) > parseFloat(filter.maxPrice)
                }) }
                placeholder="Min"
                onInput={ linkState(this, 'filterModel.minPrice') } />
              <input type="number"
                className={ cx('form-control flex-fill', {
                  'is-invalid': parseFloat(filter.maxPrice) < parseFloat(filter.minPrice)
                }) }
                placeholder="Max"
                onInput={ linkState(this, 'filterModel.maxPrice') } />
            </div>
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Min Floor Size</label>
            <input type="number" className="form-control" placeholder="SQM"
              onInput={ linkState(this, 'filterModel.minFloorArea') } />
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Bedrooms</label>
            <div className="d-flex">
              <div className="d-flex flex-column w-50 form-check">
                { (['Studio', '1', '2']).map(bedrooms => (
                  <label className="hi-input-group__checkbox mr-0">
                    <input className="form-check-input" type="radio" name="bedrooms"
                      checked={ filter.bedrooms === bedrooms }
                      onClick={ () => this.onInput({ bedrooms })} />
                    { bedrooms }
                  </label>
                )) }
              </div>
              <div className="d-flex flex-column w-50 form-check">
                { (['3', '4', '5+']).map(bedrooms => (
                  <label className="hi-input-group__checkbox mr-0">
                    <input className="form-check-input" type="radio" name="bedrooms"
                      checked={ filter.bedrooms === bedrooms }
                      onClick={ () => this.onInput({ bedrooms }) }  />
                    { bedrooms }
                  </label>
                )) }
              </div>
            </div>
          </div>

          <div className="hi-form-group">
            <label className="hi-input-group__label">Receipts</label>
            <div className="d-flex flex-column form-check">
              { (['All', 'With receipts', 'Without receipts']).map(receipts => (
                <label className="hi-input-group__checkbox mr-0">
                  <input className="form-check-input" type="radio" name="ownerReceipt"
                    checked={ (receipts === 'All' && !filter.ownerReceipt) || filter.ownerReceipt === receipts }
                    onClick={ () => this.onInput({ ownerReceipt: receipts })} />
                  { receipts }
                </label>
              )) }
            </div>
          </div>
        </div> {/* /.hi-vlsearchpanel__content */}

        <div className="hi-vlsearchpanel__button-row">
          <button className="btn btn-secondary hi-vlsearchpanel__search-button"
            onClick={ () => this.onSearchClick() }>
            Search
          </button>
        </div>
      </div>
    )
  }
}